// Замер длительности фаз и сводки по ходам / матчу.
// Все сообщения пишутся в state.log с обрамлением круглыми скобками — log.js их
// подсвечивает классом log-timing (серый курсив).
//
// ТАЙ.1: добавлены HARD-таймеры фаз (Диздок раздел 2 «Тайминг»):
//   • Ф1+Ф2 (тяга + размещение) — 25 секунд суммарно (один таймер на обе)
//   • Ф3 (вскрытие / auto) — серверная анимация, без human-таймера
//   • Ф4 / Ф5 (атаки Героев) — 10с базовых, 15с если у активного игрока
//     есть готовые к активации Скрытные юниты
// state.phaseDeadlineAt = unix-мс или null (фаза без таймера, авто).

const STEALTH_KEYWORD = 'stealth';

// Сколько мс отвести фазе. activeIdx — кто сейчас активен (для Ф4/Ф5);
// для Ф2 не используется (общий таймер обоим).
function phaseDurationMs(state, num, activeIdx) {
  if (num === 1) return 25_000;                       // Ф1+Ф2 = 25с (общий)
  if (num === 2) return null;                          // Ф2 продолжает Ф1 — deadline не сбрасываем
  if (num === 4 || num === 5) {
    if (activeIdx == null) return 10_000;
    // 15с если у активного игрока есть готовый к активации Скрытный юнит.
    const p = state.players?.[activeIdx];
    let hasStealthReady = false;
    if (p?.field) {
      outer: for (const line of p.field) {
        for (const u of line) {
          if (!u || u.alive === false) continue;
          const kw = u.current?.keywords || u.keywords || [];
          if (kw.includes(STEALTH_KEYWORD) && !u.stealthUsed) {
            hasStealthReady = true; break outer;
          }
        }
      }
    }
    return hasStealthReady ? 15_000 : 10_000;
  }
  return null; // Ф3 (auto), Ф6 (end_turn auto), etc.
}

export function initTimings(state) {
  state.timings = {
    matchStartedAt: Date.now(),
    turnStartedAt: null,
    currentTurnPhases: {},          // {1: 0.4, 2: 12.3, ...}
    currentPhase: null,             // {num, startedAt}
    byTurn: []                      // [{turn, total, phases}]
  };
  state.phaseDeadlineAt = null;     // ТАЙ.1: unix-мс или null
}

// Старт замера фазы. num — номер фазы (1..5).
export function startPhase(state, num) {
  // Если предыдущая ещё не закрыта — закрываем (страховка).
  if (state.timings.currentPhase) finishPhase(state);
  state.timings.currentPhase = { num, startedAt: Date.now() };
  // ТАЙ.1: устанавливаем deadline.
  // Для Ф2 — не сбрасываем (Ф1+Ф2 общий 25с-таймер от Ф1).
  // Если deadline не выставлен (Ф1 пропущена в первом ходу — мулиган),
  // выставим всё равно от Ф2.
  if (num === 2) {
    if (!state.phaseDeadlineAt) {
      state.phaseDeadlineAt = Date.now() + 25_000;
    }
    return;
  }
  const activeIdx = (num === 4)
    ? state.firstPlayerIdx
    : (num === 5 ? 1 - state.firstPlayerIdx : null);
  const dur = phaseDurationMs(state, num, activeIdx);
  state.phaseDeadlineAt = dur != null ? (Date.now() + dur) : null;
}

// Завершить замер текущей фазы и вывести строку лога.
export function finishPhase(state) {
  const cur = state.timings.currentPhase;
  if (!cur) {
    state.phaseDeadlineAt = null;
    return;
  }
  const elapsed = (Date.now() - cur.startedAt) / 1000;
  state.timings.currentTurnPhases[cur.num] = elapsed;
  state.log.add(`(Фаза ${cur.num}: ${formatSec(elapsed)})`);
  state.timings.currentPhase = null;
  state.phaseDeadlineAt = null;
}

// Старт замера хода.
export function startTurnTimer(state) {
  state.timings.turnStartedAt = Date.now();
  state.timings.currentTurnPhases = {};
}

// Сводка по ходу — добавляет строку и сохраняет в byTurn.
export function logTurnSummary(state, turnNum) {
  if (!state.timings.turnStartedAt) return;
  const total = (Date.now() - state.timings.turnStartedAt) / 1000;
  const phases = state.timings.currentTurnPhases;
  const parts = [];
  for (let i = 1; i <= 5; i++) {
    if (phases[i] != null) parts.push(`Ф${i}: ${formatSec(phases[i])}`);
  }
  state.log.add(`(Ход ${turnNum}: ${formatSec(total)} — ${parts.join(' / ')})`);
  state.timings.byTurn.push({
    turn: turnNum,
    total,
    phases: { ...phases }
  });
  state.timings.turnStartedAt = null;
}

// Сводка по матчу — пишется в момент завершения матча (в checkMatchOver).
export function logMatchSummary(state) {
  const turns = state.timings.byTurn;
  if (turns.length === 0) {
    // Матч закончился до завершения первого хода.
    return;
  }
  const totalSec = (Date.now() - state.timings.matchStartedAt) / 1000;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec - minutes * 60;
  const avgTurn = turns.reduce((s, t) => s + t.total, 0) / turns.length;

  // Средние по каждой фазе.
  const sums = {}, counts = {};
  for (const t of turns) {
    for (const [pn, sec] of Object.entries(t.phases)) {
      sums[pn] = (sums[pn] || 0) + sec;
      counts[pn] = (counts[pn] || 0) + 1;
    }
  }
  const avg2 = counts[2] ? formatSec(sums[2] / counts[2]) : 'нет';
  const avg3 = counts[3] ? formatSec(sums[3] / counts[3]) : 'нет';

  const totalStr = minutes > 0
    ? `${minutes} мин ${seconds.toFixed(0)} с`
    : `${seconds.toFixed(1)} с`;

  state.log.add(
    `(Матч: ${totalStr}, средний ход: ${formatSec(avgTurn)}, средняя Фаза 2: ${avg2}, средняя Фаза 3: ${avg3})`
  );
}

function formatSec(v) {
  return `${v.toFixed(1)}с`;
}
